import { useEffect, useRef, useCallback } from 'react';
import debounce from '../utils/debounce';
import logger from '../utils/logger';

/**
 * Manages the read marker for a room, updating it when the user leaves the room while at the bottom.
 * @param {object} params - The parameters for the hook.
 * @param {HTMLElement} params.scrollContainer - The DOM element for the message list.
 * @param {string} params.roomId - The ID of the current room.
 * @param {Array} params.messages - The messages in the room.
 * @param {string} params.readMarkerId - The current read marker event ID.
 * @param {Function} params.setReadMarker - Function to update the read marker.
 * @param {object} params.client - The Matrix client instance.
 * @returns {object} An object containing the `isAtBottomRef` and `updateReadMarkerOnSend`.
 */
export const useReadMarker = ({
  scrollContainer,
  roomId,
  messages,
  readMarkerId,
  setReadMarker,
  client,
}) => {
  // store the current roomId so we can detect room changes vs initial load
  const prevRoomIdRef = useRef(roomId);

  const isAtBottomRef = useRef(true);

  // Phase 3: Debouncing refs for batching rapid message sends
  const debounceTimerRef = useRef(null);
  const pendingUpdateRef = useRef(null);

  // This ref holds the latest data for the cleanup function, preventing stale closures
  // when the component re-renders for a new room.
  // Note: 'messages' is intentionally excluded from dependencies to avoid updating on every message.
  // The cleanup function will use whatever messages exist at cleanup time.
  const cleanupDataRef = useRef({});
  useEffect(() => {
    cleanupDataRef.current = { messages, readMarkerId, setReadMarker, roomId };
  }, [roomId, readMarkerId, setReadMarker]);

  // Track scroll position (runs when scrollContainer changes)
  useEffect(() => {
    if (!scrollContainer) return;

    const handleScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = scrollContainer;
      const atBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5;
      isAtBottomRef.current = atBottom;
    };

    const debouncedScrollHandler = debounce(handleScroll, 100);
    scrollContainer.addEventListener('scroll', debouncedScrollHandler);
    handleScroll(); // Initial check

    return () => {
      scrollContainer.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [scrollContainer]);

  // On room change, update marker if needed
  useEffect(() => {
    const prevRoomId = prevRoomIdRef.current;
    prevRoomIdRef.current = roomId; // Update for next time

    return () => {
      const { messages, readMarkerId, setReadMarker } = cleanupDataRef.current;

      // Only run cleanup if room actually changed
      if (prevRoomId && prevRoomId !== roomId) {
        if (isAtBottomRef.current) {
          const lastMessage = messages?.[messages.length - 1]?.messages?.slice(-1)[0];
          if (lastMessage && readMarkerId !== lastMessage.getId()) {
            logger.debug(
              `[ReadMarker] Leaving room ${prevRoomId} at bottom. Updating marker to ${lastMessage.getId()}`,
            );
            setReadMarker(lastMessage.getId(), prevRoomId);
          }
        }
      }
    };
  }, [roomId]);

  // Phase 3: Implement updateReadMarkerOnSend with debouncing for rapid message sends
  const updateReadMarkerOnSend = useCallback((eventId, destinationRoomId) => {
    if (!client || !eventId || !destinationRoomId) {
      logger.warn('[useReadMarker] Missing parameters for updateReadMarkerOnSend', {
        hasClient: !!client,
        hasEventId: !!eventId,
        hasRoomId: !!destinationRoomId,
      });
      return;
    }

    // Store the latest update info (overwrites previous if exists)
    pendingUpdateRef.current = { eventId, destinationRoomId };

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer (300ms debounce)
    debounceTimerRef.current = setTimeout(async () => {
      const update = pendingUpdateRef.current;
      if (!update) return;

      try {
        logger.debug(`[useReadMarker] Updating read marker (debounced): ${update.eventId} in room ${update.destinationRoomId}`);

        // Call Matrix SDK API with both fully_read and read receipt markers
        await client.setRoomReadMarkers(
          update.destinationRoomId,  // roomId (1st param)
          update.eventId,            // m.fully_read marker (2nd param)
          update.eventId,            // m.read receipt marker (3rd param)
        );

        logger.debug('[useReadMarker] Successfully updated read marker via API');

        // Update local state only if it's the current room
        if (update.destinationRoomId === roomId) {
          logger.debug('[useReadMarker] Updating local state for current room');
          setReadMarker(update.eventId, update.destinationRoomId);
        }
      } catch (error) {
        // Enhanced error logging with full context
        logger.error('[useReadMarker] Failed to update read marker on send:', {
          eventId: update.eventId,
          roomId: update.destinationRoomId,
          currentRoomId: roomId,
          errorMessage: error.message,
          errorCode: error.errcode || 'unknown',
          errorName: error.name,
        });

        // FALLBACK: Update local state even if API fails
        // This ensures UI shows message as read even if server sync fails
        // The next Matrix sync will eventually reconcile state
        if (update.destinationRoomId === roomId) {
          logger.debug('[useReadMarker] Applying fallback: updating local state despite API failure');
          try {
            setReadMarker(update.eventId, update.destinationRoomId);
          } catch (localError) {
            logger.error('[useReadMarker] Fallback also failed:', localError);
            // This is a critical error but don't throw - graceful degradation
          }
        }
      } finally {
        // Always cleanup refs regardless of success/failure
        pendingUpdateRef.current = null;
        debounceTimerRef.current = null;
      }
    }, 300);
  }, [client, roomId, setReadMarker]);

  // Phase 3: Cleanup effect - cancel pending timers on unmount or room change
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingUpdateRef.current = null;
    };
  }, [roomId]);

  return {
    isAtBottomRef,
    updateReadMarkerOnSend,
  };
};
